import { Injectable } from "@angular/core";
import
{
  Action,
  createSelector,
  Selector,
  State,
  StateContext,
  Store,
} from "@ngxs/store";

import { HttpErrorResponse, HttpResponse } from "@angular/common/http";
import { patch, updateItem } from "@ngxs/store/operators";
import { catchError, EMPTY, mergeMap, tap } from "rxjs";
import
{
  JiraImportIssueOverviewResponse,
  JiraImportVersionOverviewResponse,
  MailService, Note,
  NoteService,
  ProductService,
  SubscriptionCancelService,
  SubscriptionService, Tag, TagService, TextService, Version,
  VersionService
} from "../../../services/api";
import
{
  ClearNotesError,
  ClearVersionsError,
  CreateNoteError,
  CreateProductError,
  CreateSubscriptionError,
  CreateTagError,
  CreateUnsubscribeError,
  CreateVersionError,
  InitialVersionsLoaded,
  NoteDeleted,
  NotesError,
  ProductDeleted,
  ProductError,
  ProductsError,
  SetCheckNote,
  SetCheckVersion,
  SetCreatedNote,
  SetCreatedProduct,
  SetCreatedSubscription,
  SetCreatedTag,
  SetCreatedVersion,
  SetEdited,
  SetExported,
  SetProductChanged,
  SetRemovedSubscription,
  SetSendMail,
  SetUnsubscribed,
  VersionDeleted,
  VersionsError
} from "./product-api-status.actions";
import { CheckStatusEnum } from "./product-api-status.definitions";
import
{
  AddTagsToProduct,
  ClearEmailSubscriptions,
  ClearJiraIssue,
  ClearJiraVersion,
  ClearProduct,
  ClearProducts,
  ClearSelectedVersion,
  ClearTags,
  ClearVersions,
  DeleteNote,
  DeleteProduct,
  DeleteSubscription,
  DeleteVersion,
  ExportJiraTranslationProduct,
  ExportJiraTranslationVersion,
  FilterFavoritesProducts,
  FilterProducts,
  FilterTypeProducts,
  GetJiraIssue,
  GetJiraVersion,
  GetProduct,
  GetVersion,
  GetVersionJiraIssue,
  ImportJiraIssue,
  ImportJiraVersion,
  LoadDeletedProducts,
  LoadDeletedVersions,
  LoadEmailSubscriptions,
  LoadJiraVersions,
  LoadNotesForVersion,
  LoadProduct,
  LoadProducts,
  LoadSubscriptionsForProduct,
  LoadTagsOfProduct,
  LoadVersionsOfProduct,
  PermanentlyDeleteProduct,
  PermanentlyDeleteVersion,
  PostNote,
  PostProduct,
  PostTag,
  PostTranslation,
  PostVersion,
  PutNote,
  PutProduct,
  PutText,
  PutVersion,
  RemoveTagsFromProduct,
  RestoreDeletedProduct,
  RestoreDeletedVersion,
  SelectVersion,
  SendEmail,
  Subscribe,
  SubscribeUser,
  Unsubscribe
} from "./product.actions";
import { defaultProductState, IProductState } from "./product.definitions";


@State<IProductState>({
  name: "product",
  defaults: defaultProductState,
})
@Injectable()
export class ProductState
{
  constructor(
    private productService: ProductService,
    private versionService: VersionService,
    private noteService: NoteService,
    private mailService: MailService,
    private subscriptionService: SubscriptionService,
    private subscriptionCancelService: SubscriptionCancelService,
    private textService: TextService,
    private tagService: TagService,
    private store: Store) { }

  @Selector([ProductState])
  public static jiraVersions(state: IProductState)
  {
    return state.jiraVersions;
  }

  @Selector([ProductState])
  public static emailSubscriptions(state: IProductState)
  {
    return state.emailSubscriptions;
  }

  @Selector([ProductState])
  public static productSubscriptions(state: IProductState)
  {
    return state.selectedProduct.subscriptions;
  }

  @Selector([ProductState])
  public static jiraVersion(state: IProductState)
  {
    return state.jiraVersion;
  }

  @Selector([ProductState])
  public static jiraNote(state: IProductState)
  {
    return state.jiraNote;
  }

  static version(versionId: number)
  {
    return createSelector([ProductState], (state: IProductState) =>
    {
      return state.selectedProduct?.versions?.find((x) => x.id === versionId)!;
    });
  }

  static notes(versionId: number)
  {
    return createSelector([ProductState], (state: IProductState) =>
    {
      const version = state.selectedProduct?.versions?.find(
        (x) => x.id === versionId
      );
      return version ? (version.notes === null ? undefined : version.notes) : undefined;
    });
  }

  @Selector([ProductState])
  static filteredProducts(state: IProductState)
  {
    let products = state.products;
    if (products)
    {
      if (state.filterProductType.length > 0)
        products = products.filter(
          (p) => p.productType?.name && state.filterProductType.includes(p.productType.name)
        );
      if (state.filterProductText.length > 0)
        products = products.filter((p) =>
          p.name?.toLowerCase().includes(state.filterProductText.toLowerCase())
        );
      if (state.filterProductFavorites.length > 0)
        products = products.filter((p) =>
          state.filterProductFavorites.includes(p.id!)
        );
    }
    return products;
  }

  @Selector([ProductState])
  static filteredProductText(state: IProductState)
  {
    return state.filterProductText;
  }

  @Selector([ProductState])
  static deletedProducts(state: IProductState)
  {
    return state.deletedProducts;
  }

  @Selector([ProductState])
  static deletedVersions(state: IProductState)
  {
    return state.deletedVersions;
  }

  @Selector([ProductState])
  public static selectedProduct(state: IProductState)
  {
    return state.selectedProduct;
  }

  @Selector([ProductState])
  public static versions(state: IProductState): Version[] | undefined
  {
    return state.selectedProduct?.versions ?? undefined;
  }

  @Selector([ProductState])
  public static tags(state: IProductState): Tag[] | undefined
  {
    return state.selectedProduct?.tags ?? undefined;
  }

  @Selector([ProductState])
  public static selectedVersionId(state: IProductState)
  {
    return state.selectedVersionId;
  }

  //GET
  @Action(LoadDeletedProducts)
  public loadDeletedProducts(ctx: StateContext<IProductState>)
  {
    return this.productService.getDeletedProducts().pipe(
      tap((deletedProducts) =>
        ctx.patchState({
          deletedProducts,
        })
      )
    );
  }

  @Action(LoadDeletedVersions)
  public loadDeletedVersions(ctx: StateContext<IProductState>)
  {
    return this.versionService.getDeletedVersions().pipe(
      tap((deletedVersions) =>
        ctx.patchState({
          deletedVersions,
        })
      )
    );
  }

  @Action(LoadProducts)
  public loadProducts({ patchState }: StateContext<IProductState>)
  {
    return this.productService.getProducts().pipe(
      catchError((err: HttpErrorResponse) =>
      {
        this.store.dispatch(new ClearProducts());
        this.store.dispatch(new ProductsError("HTTP Error: " + err.url));
        return EMPTY;
      }),
      tap((products) => patchState({ products }))
    );
  }

  @Action(LoadVersionsOfProduct)
  public loadVersions(ctx: StateContext<IProductState>, action: LoadVersionsOfProduct)
  {
    return this.productService.getVersionsOfProduct(action.productId ?? 0).pipe(
      catchError((err: HttpErrorResponse) =>
      {
        this.store.dispatch(new ClearVersions());
        this.store.dispatch(new NotesError("HTTP Error: " + err.url));
        this.store.dispatch(new VersionsError("HTTP Error: " + err.url));
        return EMPTY;
      }),
      tap((versions) =>
      {
        ctx.setState(
          patch<IProductState>({
            selectedProduct: patch<IProductState["selectedProduct"]>({
              versions: versions,
            }),
          })
        );
      })
    );
  }

  @Action(LoadTagsOfProduct)
  public loadTagsOfProduct(ctx: StateContext<IProductState>, action: LoadTagsOfProduct)
  {
    return this.productService.getTagsOfProduct(action.productId ?? 0).pipe(
      catchError((err: HttpErrorResponse) =>
      {
        this.store.dispatch(new ClearTags());
        this.store.dispatch(new ProductError("HTTP Error: " + err.url));
        return EMPTY;
      }),
      tap((tags) =>
      {
        ctx.setState(
          patch<IProductState>({
            selectedProduct: patch<IProductState["selectedProduct"]>({
              tags: tags,
            }),
          })
        );
      })
    );
  }


  @Action(LoadJiraVersions)
  public loadJiraVersions({ patchState }: StateContext<IProductState>,
    action: LoadJiraVersions)
  {
    return this.productService
      .getAvailableJiraVersions(action.productId ?? 0)
      .pipe(
        tap((jiraVersions) =>
        {
          patchState({ jiraVersions });
        })
      );
  }

  @Action(LoadEmailSubscriptions)
  public loadEmailSubscriptions({ patchState }: StateContext<IProductState>, action: LoadEmailSubscriptions)
  {
    return this.productService
      .getSubscriptionsByLanguage(action.productId ?? 0, action.languageId)
      .pipe(
        tap((emailSubscriptions) =>
        {
          patchState({ emailSubscriptions });
        })
      );
  }

  @Action(LoadNotesForVersion)
  public loadNotesForVersion(ctx: StateContext<IProductState>, action: LoadNotesForVersion)
  {
    return this.versionService.getNotesOfVersion(action.versionId!).pipe(
      catchError((err: HttpErrorResponse) =>
      {
        this.store.dispatch(new NotesError("HTTP Error: " + err.url));
        return EMPTY;
      }),
      tap((newNotes: Note[]) =>
      {
        this.store.dispatch(new ClearNotesError());

        if (!newNotes) newNotes = [];
        ctx.setState(
          patch<IProductState>({
            selectedProduct: patch<IProductState["selectedProduct"]>({
              versions: updateItem<Version>(
                (version) => version.id === action.versionId,
                patch({ notes: newNotes })
              ),
            }),
          })
        );
      })
    );
  }

  //Requests GetProduct and GetVersionsOfProduct
  @Action(LoadProduct)
  public async loadProduct(ctx: StateContext<IProductState>, action: LoadProduct)
  {
    return this.productService.getProduct(action.productId!).pipe(
      catchError((err: HttpErrorResponse) =>
      {
        this.store.dispatch(new ClearProduct());
        this.store.dispatch(new VersionsError("HTTP Error: " + err.url));
        this.store.dispatch(new ProductError("HTTP Error: " + err.url));
        return EMPTY;
      }),
      tap((selectedProduct) =>
      {
        this.store.dispatch(new InitialVersionsLoaded(true));
        ctx.patchState({ selectedProduct });
      }),
      mergeMap(() =>
        ctx.dispatch([
          new LoadVersionsOfProduct(action.productId!),
          new LoadTagsOfProduct(action.productId!)
        ])
      )
    );
  }

  //Requests GetProduct without updating versions
  @Action(GetProduct)
  public async getProduct(ctx: StateContext<IProductState>, action: GetProduct)
  {
    const versions = structuredClone(ctx.getState().selectedProduct.versions);
    return this.productService.getProduct(action.productId!).pipe(
      catchError((err: HttpErrorResponse) =>
      {
        this.store.dispatch(new VersionsError("HTTP Error: " + err.url));
        this.store.dispatch(new ProductError("HTTP Error: " + err.url));
        return EMPTY;
      }),
      tap((selectedProduct) =>
      {
        selectedProduct.versions = versions;
        ctx.patchState({ selectedProduct });
      })
    );
  }

  @Action(LoadSubscriptionsForProduct)
  public getSubscriptions(ctx: StateContext<IProductState>, action: LoadSubscriptionsForProduct)
  {
    return this.productService.getSubscriptionsOfProduct(action.productId).pipe(
      catchError(() =>
      {
        return EMPTY;
      }),
      tap((subscriptions) =>
      {
        ctx.setState(
          patch<IProductState>({
            selectedProduct: patch<IProductState["selectedProduct"]>({
              subscriptions: subscriptions ?? [],
            }),
          })
        );
      })
    );
  }

  // @Action(CheckVersionOfProduct)
  // public checkVersion(
  //   { patchState }: StateContext<IProductState>,
  //   action: CheckVersionOfProduct
  // ) {
  //   return this.productService
  //     .validateJiraVersionIdOfProduct(action.productId, action.jiraKey)
  //     .pipe(
  //       catchError(() => {
  //         this.store.dispatch(new SetCheckVersion(CheckStatus.Invalid));
  //         return EMPTY;
  //       }),
  //       tap((checkedVersion) => {
  //         this.store.dispatch(
  //           new SetCheckVersion(
  //             checkedVersion ? CheckStatus.Available : CheckStatus.AlreadyExits
  //           )
  //         );
  //       })
  //     );
  // }

  // @Action(CheckNoteOfVersion)
  // public checkNoteOfVersion(
  //   { patchState }: StateContext<IProductState>,
  //   action: CheckNoteOfVersion
  // ) {
  //   return this.versionService
  //     .validateJiraIssueKeyOfVersion(action.versionId, action.jiraKey)
  //     .pipe(
  //       catchError(() => {
  //         this.store.dispatch(new SetCheckNote(CheckStatus.Invalid));
  //         return EMPTY;
  //       }),
  //       tap((checkedNote) => {
  //         this.store.dispatch(
  //           new SetCheckNote(
  //             checkedNote ? CheckStatus.Available : CheckStatus.AlreadyExits
  //           )
  //         );
  //       })
  //     );
  // }

  // @Action(CheckNote)
  // public checkNote({patchState}: StateContext<IProductState>, action: CheckNote) {
  //     return this.noteService.validateJiraIssueKey(action.jiraKey, "response").pipe(
  //         catchError(() => {
  //                 this.store.dispatch(new SetCheckNote(CheckStatus.Invalid))
  //                 return EMPTY;
  //             }
  //         ),
  //         tap((checkedNote) => {
  //                 this.store.dispatch(new SetCheckNote(checkedNote.status == 200 ? CheckStatus.Available : CheckStatus.Invalid))
  //             }
  //         )
  //     );
  // }

  @Action(SelectVersion)
  public selectVersion(ctx: StateContext<IProductState>, action: SelectVersion)
  {
    ctx.patchState({ selectedVersionId: action.versionId });
  }

  @Action(GetVersion)
  public getVersion(ctx: StateContext<IProductState>, action: GetVersion)
  {
    return this.versionService.getVersion(action.versionId!).pipe(
      catchError((err: HttpErrorResponse) =>
      {
        this.store.dispatch(new NotesError("HTTP Error: " + err.url));
        this.store.dispatch(new VersionsError("HTTP Error: " + err.url));
        return EMPTY;
      }),
      tap((updatedVersion: Version) =>
      {
        if (updatedVersion.versionType)
        {
          this.store.dispatch(new ClearVersionsError());
          this.store.dispatch(new ClearNotesError());
        }
        ctx.patchState({ selectedVersionId: action.versionId });
        ctx.setState(
          patch<IProductState>({
            selectedProduct: patch<IProductState["selectedProduct"]>({
              versions: updateItem<Version>(
                (version) => version.id === action.versionId,
                patch(updatedVersion)
              ),
            }),
          })
        );
      })
    );
  }

  @Action(GetJiraVersion)
  public getJiraVersion({ patchState }: StateContext<IProductState>, action: GetJiraVersion)
  {
    return this.productService
      .getJiraVersionOverview(action.productId, action.jiraKey)
      .pipe(
        catchError((err: HttpErrorResponse) =>
        {
          this.store.dispatch(new SetCheckVersion(err.status === 404 ? CheckStatusEnum.Invalid : CheckStatusEnum.AlreadyExits));
          return EMPTY;
        }),
        tap((jiraVersion: JiraImportVersionOverviewResponse) =>
        {
          this.store.dispatch(new SetCheckVersion(CheckStatusEnum.Available));
          patchState({ jiraVersion });
        })
      );
  }

  @Action(GetVersionJiraIssue)
  public getVersionJiraIssue({ patchState }: StateContext<IProductState>, action: GetVersionJiraIssue)
  {
    return this.versionService.getVersionJiraIssueOverview(action.versionId, action.jiraKey).pipe(
      catchError((err: HttpErrorResponse) =>
      {
        this.store.dispatch(new SetCheckNote(err.status === 404 ? CheckStatusEnum.Invalid : CheckStatusEnum.AlreadyExits));
        return EMPTY;
      }),
      tap((jiraNote: JiraImportIssueOverviewResponse) =>
      {
        this.store.dispatch(new SetCheckNote(CheckStatusEnum.Available));
        patchState({ jiraNote });
      }
      )
    );
  }

  @Action(GetJiraIssue)
  public getJiraIssue({ patchState }: StateContext<IProductState>, action: GetJiraIssue)
  {
    return this.noteService.getJiraIssueOverview(action.jiraKey).pipe(
      catchError((err: HttpErrorResponse) =>
      {
        this.store.dispatch(new SetCheckNote(err.status === 404 ? CheckStatusEnum.Invalid : CheckStatusEnum.AlreadyExits));
        return EMPTY;
      }),
      tap((jiraNote: JiraImportIssueOverviewResponse) =>
      {
        this.store.dispatch(new SetCheckNote(CheckStatusEnum.Available));
        patchState({ jiraNote });
      }
      )
    );
  }

  @Action(RestoreDeletedProduct)
  public restoreDeletedProduct(ctx: StateContext<IProductState>, action: RestoreDeletedProduct)
  {
    const deletedProducts = structuredClone(ctx.getState().deletedProducts);
    return this.productService
      .restoreProduct(action.productId!, "response")
      .pipe(
        catchError((err: HttpErrorResponse) => EMPTY),
        tap((response: HttpResponse<any>) =>
        {
          const index = deletedProducts.findIndex(
            (x) => x.id === action.productId
          );
          deletedProducts.splice(index, 1);
          ctx.patchState({ deletedProducts });
        })
      );
  }

  @Action(RestoreDeletedVersion)
  public restoreDeletedVersion(ctx: StateContext<IProductState>, action: RestoreDeletedVersion)
  {
    const deletedVersions = structuredClone(ctx.getState().deletedVersions);
    return this.versionService
      .restoreVersion(action.versionId!, "response")
      .pipe(
        catchError((err: HttpErrorResponse) => EMPTY),
        tap((response: HttpResponse<any>) =>
        {
          const index = deletedVersions.findIndex(
            (x) => x.id === action.versionId
          );
          deletedVersions.splice(index, 1);
          ctx.patchState({ deletedVersions });
        })
      );
  }

  //DELETE
  @Action(PermanentlyDeleteProduct)
  public permanentlyDeleteProduct(ctx: StateContext<IProductState>, action: PermanentlyDeleteProduct)
  {
    const deletedProducts = structuredClone(ctx.getState().deletedProducts);
    return this.productService
      .deleteProductPermanently(action.productId!, "response")
      .pipe(
        catchError(() => EMPTY),
        tap(() =>
        {
          const index = deletedProducts.findIndex(
            (x) => x.id === action.productId
          );
          deletedProducts.splice(index, 1);
          ctx.patchState({ deletedProducts });
        })
      );
  }

  @Action(DeleteProduct)
  public deleteProduct({ patchState }: StateContext<IProductState>, action: DeleteProduct)
  {
    return this.productService
      .deleteProduct(action.productId!, "response")
      .pipe(
        catchError(() =>
        {
          alert("Delete failed");
          return EMPTY;
        }),
        tap((response: HttpResponse<any>) =>
        {
          this.store.dispatch(new ProductDeleted(response.status === 204));
        })
      );
  }

  @Action(PermanentlyDeleteVersion)
  public permanentlyDeleteVersion(ctx: StateContext<IProductState>, action: PermanentlyDeleteVersion)
  {
    const deletedVersions = structuredClone(ctx.getState().deletedVersions);
    return this.versionService
      .deleteVersionPermanently(action.versionId!, "response")
      .pipe(
        catchError(() => EMPTY),
        tap(() =>
        {
          const index = deletedVersions.findIndex(
            (x) => x.id === action.versionId
          );
          deletedVersions.splice(index, 1);
          ctx.patchState({ deletedVersions });
        })
      );
  }

  @Action(DeleteVersion)
  public deleteVersion({ patchState }: StateContext<IProductState>, action: DeleteVersion)
  {
    return this.versionService
      .deleteVersion(action.versionId!, "response")
      .pipe(
        catchError(() =>
        {
          alert("Delete failed");
          return EMPTY;
        }),
        tap((response: HttpResponse<any>) =>
        {
          this.store.dispatch(new VersionDeleted(response.status === 204));
        })
      );
  }

  @Action(DeleteNote)
  public deleteNote({ patchState }: StateContext<IProductState>, action: DeleteNote)
  {
    return this.noteService.deleteNote(action.noteId!, "response").pipe(
      catchError(() =>
      {
        alert("Delete failed");
        return EMPTY;
      }),
      tap((response: HttpResponse<any>) =>
      {
        this.store.dispatch(new NoteDeleted(response.status === 204));
      })
    );
  }

  @Action(DeleteSubscription)
  public deleteSubscription({ patchState }: StateContext<IProductState>, action: DeleteSubscription)
  {
    return this.subscriptionService
      .deleteSubscription(
        action.subscription.productId,
        action.subscription.languageId,
        action.subscription.userMail ?? undefined,
        "response"
      )
      .pipe(
        catchError((err: HttpErrorResponse) =>
        {
          this.store.dispatch(
            new CreateUnsubscribeError("HTTP Error: " + err.url)
          );
          return EMPTY;
        }),
        tap((response: HttpResponse<any>) =>
        {
          this.store.dispatch(
            new SetRemovedSubscription(response.status === 200)
          );
        })
      );
  }

  @Action(Unsubscribe)
  public unsubscribe({ patchState }: StateContext<IProductState>, action: Unsubscribe)
  {
    return this.subscriptionCancelService
      .unsubscribe(action.cancellationToken, "response")
      .pipe(
        catchError((err: HttpErrorResponse) =>
        {
          this.store.dispatch(
            new CreateUnsubscribeError("HTTP Error: " + err.url)
          );
          return EMPTY;
        }),
        tap((response: HttpResponse<any>) =>
        {
          this.store.dispatch(new SetUnsubscribed(response.status === 200));
        })
      );
  }

  //POST
  @Action(Subscribe)
  public subscribe({ patchState }: StateContext<IProductState>, action: Subscribe)
  {
    return this.productService
      .subscribeToProduct(action.productId, action.langId, "response")
      .pipe(
        catchError((err: HttpErrorResponse) =>
        {
          if (err.status === 409)
            this.store.dispatch(new CreateSubscriptionError("Only AUMA-Domains are allowed to subscribe"));
          else
            this.store.dispatch(new CreateSubscriptionError("Subscription could not be added"));
          return EMPTY;
        }),
        tap((response: HttpResponse<any>) =>
        {
          this.store.dispatch(
            new SetCreatedSubscription(response.status === 200)
          );
        })
      );
  }

  @Action(SubscribeUser)
  public subscribeUser({ patchState }: StateContext<IProductState>, action: SubscribeUser)
  {
    return this.productService
      .subscribeUserToProduct(action.userReq, "response")
      .pipe(
        catchError((err: HttpErrorResponse) =>
        {
          this.store.dispatch(
            new CreateSubscriptionError("HTTP Error: " + err.url)
          );
          return EMPTY;
        }),
        tap((response: HttpResponse<any>) =>
        {
          this.store.dispatch(
            new SetCreatedSubscription(response.status === 200)
          );
        })
      );
  }

  @Action(PostVersion)
  public postVersion({ patchState }: StateContext<IProductState>, action: PostVersion)
  {
    return this.versionService.postVersion(action.version, "response").pipe(
      catchError((err: HttpErrorResponse) =>
      {
        this.store.dispatch(new CreateVersionError("HTTP Error: " + err.url));
        return EMPTY;
      }),
      tap((response: HttpResponse<any>) =>
      {
        this.store.dispatch(new SetCreatedVersion(response.status === 201));
      })
    );
  }

  @Action(PostProduct)
  public postProduct({ patchState }: StateContext<IProductState>, action: PostProduct)
  {
    return this.productService.postProduct(action.product, "response").pipe(
      catchError((err: HttpErrorResponse) =>
      {
        this.store.dispatch(new CreateProductError("HTTP Error: " + err.url));
        return EMPTY;
      }),
      tap((response: HttpResponse<any>) =>
      {
        this.store.dispatch(new SetCreatedProduct(response.status === 201));
      })
    );
  }

  @Action(SendEmail)
  public sendEmail({ patchState }: StateContext<IProductState>, action: SendEmail)
  {
    return this.mailService.sendMail(action.mail, "response").pipe(
      catchError(() =>
      {
        // return this.store.dispatch(new CreateEmailError("HTTP Error: " + err.url))
        this.store.dispatch(new SetSendMail(false));
        return EMPTY;
      }),
      tap((response: HttpResponse<any>) =>
      {
        this.store.dispatch(new SetSendMail(response.status === 200));
      })
    );
  }

  @Action(PostNote)
  public postNote({ patchState }: StateContext<IProductState>, action: PostNote)
  {
    return this.noteService.postNote(action.note, "response").pipe(
      catchError((err: HttpErrorResponse) =>
      {
        this.store.dispatch(new CreateNoteError("HTTP Error: " + err.url));
        return EMPTY;
      }),
      tap((response: HttpResponse<any>) =>
      {
        this.store.dispatch(new SetCreatedNote(response.status === 201));
      })
    );
  }

  @Action(PostTag)
  public postTag({ patchState }: StateContext<IProductState>, action: PostTag)
  {
    return this.tagService.postTag({ productId: action.productId, name: action.name, description: action.description }, "response").pipe(
      catchError((err: HttpErrorResponse) =>
      {
        this.store.dispatch(new CreateTagError("HTTP Error: " + err.url));
        return EMPTY;
      }),
      tap((response: HttpResponse<any>) =>
      {
        this.store.dispatch(new SetCreatedTag(response.status === 201));
      })
    );
  }

  @Action(AddTagsToProduct)
  public addTagsToProduct({ patchState }: StateContext<IProductState>, action: AddTagsToProduct)
  {
    return this.productService.addTagsToProduct(action.tagIds, action.productId, "response").pipe(
      catchError((err: HttpErrorResponse) =>
      {
        this.store.dispatch(new SetProductChanged(false));
        return EMPTY;
      }),
      tap((response: HttpResponse<any>) =>
      {
        this.store.dispatch(new SetProductChanged(response.status === 201));
      })
    );
  }

  @Action(RemoveTagsFromProduct)
  public removeTagsFromProduct({ patchState }: StateContext<IProductState>, action: RemoveTagsFromProduct)
  {
    return this.productService.removeTagsFromProduct(action.tagIds, action.productId, "response").pipe(
      catchError((err: HttpErrorResponse) =>
      {
        this.store.dispatch(new SetProductChanged(false));
        return EMPTY;
      }),
      tap((response: HttpResponse<any>) =>
      {
        this.store.dispatch(new SetProductChanged(response.status === 201));
      })
    );
  }

  @Action(PostTranslation)
  public postTranslation(ctx: StateContext<IProductState>, action: PostTranslation)
  {
    return this.textService
      .addTranslation(
        action.translation.textId!,
        action.translation,
        "response"
      )
      .pipe(
        catchError(() =>
        {
          this.store.dispatch(new SetEdited(false));
          return EMPTY;
        }),
        tap((response: HttpResponse<any>) =>
        {
          this.store.dispatch(new SetEdited(response.status === 204));
        })
      );
  }

  @Action(ImportJiraVersion)
  public importJiraVersion(ctx: StateContext<IProductState>, action: ImportJiraVersion)
  {
    const jiraVersions = structuredClone(ctx.getState().jiraVersions);
    return this.productService
      .importJiraVersion(
        action.version,
        "response"
      )
      .pipe(
        catchError((err: HttpErrorResponse) =>
        {
          this.store.dispatch(new CreateVersionError("HTTP Error: " + err.url));
          return EMPTY;
        }),
        tap((response: HttpResponse<any>) =>
        {
          this.store.dispatch(new SetCreatedVersion(response.status === 200));
          const index = jiraVersions.findIndex(
            (x) => x.jiraId === action.version.jiraVersionId
          );
          jiraVersions.splice(index, 1);
          ctx.patchState({ jiraVersions });
        })
      );
  }

  @Action(ImportJiraIssue)
  public importJiraIssue(ctx: StateContext<IProductState>, action: ImportJiraIssue)
  {
    return this.versionService
      .importJiraIssue(
        action.versionId,
        action.jiraIssueKey,
        action.visibilityId,
        "response"
      )
      .pipe(
        catchError((err: HttpErrorResponse) =>
        {
          this.store.dispatch(new CreateNoteError("HTTP Error: " + err.url));
          return EMPTY;
        }),
        tap((response: HttpResponse<any>) =>
        {
          this.store.dispatch(new SetCreatedNote(response.status === 200));
          // const index = jiraVersions.findIndex(x => x.jiraId === action.jiraProjectKey);
          // jiraVersions.splice(index, 1);
          // ctx.patchState({jiraVersions});
        })
      );
  }

  @Action(ExportJiraTranslationVersion)
  public exportJiraTranslationVersion(ctx: StateContext<IProductState>, action: ExportJiraTranslationVersion)
  {
    return this.versionService
      .exportVersionTranslationFileToJira(
        action.jiraKey,
        action.versionId,
        action.languageId,
        "response"
      )
      .pipe(
        catchError(() =>
        {
          // return this.store.dispatch(new EditError("HTTP Error: " + err.url))
          this.store.dispatch(new SetExported(false));
          return EMPTY;
        }),
        tap((response: HttpResponse<any>) =>
        {
          this.store.dispatch(new SetExported(response.status === 200));
        })
      );
  }

  @Action(ExportJiraTranslationProduct)
  public exportJiraTranslationProduct(ctx: StateContext<IProductState>, action: ExportJiraTranslationProduct)
  {
    return this.productService
      .exportProductTranslationFileToJira(
        action.jiraKey,
        action.productId,
        action.languageId,
        "response"
      )
      .pipe(
        catchError(() =>
        {
          this.store.dispatch(new SetExported(false));
          return EMPTY;
        }),
        tap((response: HttpResponse<any>) =>
        {
          this.store.dispatch(new SetExported(response.status === 200));
        })
      );
  }

  //PUT
  @Action(PutProduct)
  public putProduct(ctx: StateContext<IProductState>, action: PutProduct)
  {
    return this.productService
      .putProduct(action.product.id!, action.product, "response")
      .pipe(
        catchError(() =>
        {
          // return this.store.dispatch(new EditError("HTTP Error: " + err.url))
          this.store.dispatch(new SetEdited(false));
          return EMPTY;
        }),
        tap((response: HttpResponse<any>) =>
        {
          // if (response.status === 201) {
          //     ctx.setState(patch<IProductState>({
          //             selectedProduct: action.product
          //         })
          //     );
          // }
          this.store.dispatch(new SetEdited(response.status === 204));
        })
      );
  }

  @Action(PutVersion)
  public putVersion(ctx: StateContext<IProductState>, action: PutVersion)
  {
    return this.versionService
      .putVersion(action.version.id!, action.version, "response")
      .pipe(
        catchError(() =>
        {
          this.store.dispatch(new SetEdited(false));
          return EMPTY;
        }),
        tap((response: HttpResponse<any>) =>
        {
          this.store.dispatch(new SetEdited(response.status === 204));
        })
      );
  }

  @Action(PutNote)
  public putNote(ctx: StateContext<IProductState>, action: PutNote)
  {
    return this.noteService
      .putNote(action.note.id!, action.note, "response")
      .pipe(
        catchError(() =>
        {
          this.store.dispatch(new SetEdited(false));
          return EMPTY;
        }),
        tap((response: HttpResponse<any>) =>
        {
          this.store.dispatch(new SetEdited(response.status === 204));
        })
      );
  }

  @Action(PutText)
  public putText(ctx: StateContext<IProductState>, action: PutText)
  {
    return this.textService
      .putText(action.text.id!, action.text, "response")
      .pipe(
        catchError(() =>
        {
          this.store.dispatch(new SetEdited(false));
          return EMPTY;
        }),
        tap((response: HttpResponse<any>) =>
        {
          this.store.dispatch(new SetEdited(response.status === 204));
        })
      );
  }

  //No HTTP
  @Action(FilterProducts)
  updateProductsFilter({ patchState }: StateContext<IProductState>, action: FilterProducts)
  {
    patchState({ filterProductText: action.filterValue });
  }

  @Action(FilterTypeProducts)
  updateProductsTypeFilter({ patchState }: StateContext<IProductState>, action: FilterTypeProducts)
  {
    patchState({ filterProductType: action.filterValue });
  }

  @Action(FilterFavoritesProducts)
  updateProductsFavoritesFilter({ patchState }: StateContext<IProductState>, action: FilterFavoritesProducts)
  {
    patchState({ filterProductFavorites: action.favoriteIds });
  }

  @Action(ClearSelectedVersion)
  clearSelectedVersion({ patchState }: StateContext<IProductState>, action: ClearSelectedVersion)
  {
    patchState({ selectedVersionId: null });
  }

  @Action(ClearEmailSubscriptions)
  clearEmailSubscriptions({ patchState }: StateContext<IProductState>, action: ClearEmailSubscriptions)
  {
    patchState({ emailSubscriptions: [] });
  }

  @Action(ClearProduct)
  clearProduct({ patchState }: StateContext<IProductState>)
  {
    patchState({
      selectedProduct: {
        name: undefined,
        description: undefined,
        jiraConnections: [],
        createdBy: undefined,
        createdOn: undefined,
        modifiedBy: undefined,
        modifiedOn: undefined,
        productType: undefined,
        versions: undefined,
      },
    });
  }

  @Action(ClearVersions)
  public clearVersions(ctx: StateContext<IProductState>, action: ClearVersions)
  {
    ctx.setState(
      patch<IProductState>({
        selectedProduct: patch<IProductState["selectedProduct"]>({
          versions: [],
        }),
      })
    );
  }

  @Action(ClearTags)
  public clearTags(ctx: StateContext<IProductState>, action: ClearTags)
  {
    ctx.setState(
      patch<IProductState>({
        selectedProduct: patch<IProductState["selectedProduct"]>({
          tags: [],
        }),
      })
    );
  }

  @Action(ClearProducts)
  clearProducts({ patchState }: StateContext<IProductState>, action: ClearProducts)
  {
    patchState({ products: [] });
  }

  @Action(ClearJiraVersion)
  clearJiraVersion({ patchState }: StateContext<IProductState>)
  {
    patchState({ jiraVersion: {} });
  }

  @Action(ClearJiraIssue)
  ClearJiraIssue({ patchState }: StateContext<IProductState>)
  {
    patchState({ jiraNote: {} });
  }
}
